import React from 'react';
import { useNavigate } from 'react-router-dom';
import { normalizeCardSize, isRowLayout } from '../data/cardSize';
import { DISCOUNTS_ENABLED } from '../content/features';
import { ratingSummary } from '../utils/rating';
import './RestaurantCard.css';

// A listing. Renders either as a compact row or as a photo tile depending on
// the site-wide card size an admin has chosen — see src/data/cardSize.js.
//
// Both layouts carry exactly the same information; what changes is how much of
// the screen the photograph gets. The row puts the discount in a right-hand
// column so the percentages line up down the page; the tile overlays it on the
// photo, where there is no column to line up with.
export default function RestaurantCard({ restaurant, size }) {
  const navigate = useNavigate();
  const {
    id, name, cuisine, address, freeDiscount, blackDiscount,
    imageUrl, city, dist, featured,
  } = restaurant;

  const cardSize = normalizeCardSize(size);
  const row = isRowLayout(cardSize);
  const hasFree = DISCOUNTS_ENABLED && Number(freeDiscount) > 0;
  const hasBlack = DISCOUNTS_ENABLED && Number(blackDiscount) > 0;
  const primaryLocation = city || address;
  const rating = DISCOUNTS_ENABLED ? null : ratingSummary(restaurant);

  const label = rating
    ? `${name} — ${rating.label}`
    : hasFree
    ? `${name} — ${freeDiscount}% off`
    : hasBlack
    ? `${name} — ${blackDiscount}% off with Volcyy Black`
    : name;

  const photo = imageUrl ? (
    <img
      src={imageUrl}
      alt=""
      className="restaurant-card__img"
      loading="lazy"
      decoding="async"
    />
  ) : (
    <div className="restaurant-card__img-placeholder" aria-hidden="true">
      <svg width={row ? 24 : 34} height={row ? 24 : 34} viewBox="0 0 40 40" fill="none">
        <path d="M8 32V16c0-1.1.9-2 2-2h20c1.1 0 2 .9 2 2v16" stroke="#c4c4c4" strokeWidth="2" strokeLinecap="round"/>
        <path d="M4 32h32M16 32v-8h8v8" stroke="#c4c4c4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
  );

  const hasDist = typeof dist === 'number';

  // In a row the right-hand column carries the distance underneath the
  // discounts. It used to sit in the body, on a line of its own, where it took
  // up a whole row to say "0.9 mi" and left the city sharing a cramped line
  // with the cuisine — on a phone that squeezed the city down to "Silv…".
  // Both are short, right-aligned, numeric facts; stacking them frees the body
  // to give the city a full line.
  const offers = (hasFree || hasBlack || (row && hasDist)) && (
    <div className="restaurant-card__offers">
      {hasFree && <span className="restaurant-card__pill">{freeDiscount}% Off</span>}
      {hasBlack && (
        <span className="restaurant-card__pill restaurant-card__pill--black">
          {blackDiscount}% Off
        </span>
      )}
      {row && hasDist && (
        <span className="restaurant-card__dist-chip">{dist.toFixed(1)} mi</span>
      )}
    </div>
  );

  return (
    <button
      className={`restaurant-card restaurant-card--${cardSize}`}
      onClick={() => navigate(`/business/${id}`)}
      aria-label={label}
    >
      <div className="restaurant-card__thumb">
        {photo}
        {featured && (
          <span className="restaurant-card__featured">★ Featured</span>
        )}
        {/* On a tile the offers sit over the photo; the row has a column for
            them, so it renders them after the body instead. */}
        {!row && offers}
      </div>

      <div className="restaurant-card__body">
        <h3 className="restaurant-card__name">{name}</h3>
        <p className="restaurant-card__meta">
          <span className="restaurant-card__cuisine">{cuisine}</span>
        </p>
        {!DISCOUNTS_ENABLED && (
          <p className="restaurant-card__rating">
            {rating ? (
              <>
                {rating.average && (
                  <>
                    <span className="restaurant-card__rating-star" aria-hidden="true">★</span>
                    <strong>{rating.average}</strong>
                  </>
                )}
                <span className="restaurant-card__rating-count">{rating.countText}</span>
              </>
            ) : (
              <span className="restaurant-card__rating-count">No comments yet</span>
            )}
          </p>
        )}

        {row ? (
          // The city on its own line, with the whole body width to itself.
          primaryLocation && (
            <p className="restaurant-card__place">
              <svg width="11" height="11" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <circle cx="7" cy="6" r="1.9" stroke="currentColor" strokeWidth="1.3" />
                <path d="M7 1C4.24 1 2 3.24 2 6c0 3.75 5 7 5 7s5-3.25 5-7c0-2.76-2.24-5-5-5z" stroke="currentColor" strokeWidth="1.3" />
              </svg>
              {primaryLocation}
            </p>
          )
        ) : (
          <p className="restaurant-card__footer">
            {primaryLocation && <span className="restaurant-card__where">{primaryLocation}</span>}
            {typeof dist === 'number' && (
              <span className="restaurant-card__dist">{dist.toFixed(1)} mi</span>
            )}
          </p>
        )}
      </div>

      {row && offers}
    </button>
  );
}
